import React, { useEffect, useState } from 'react';
import Modal from '../components/ui/Modal';
import Button from '../components/ui/Button';
import { useRestartLogin } from './useRestartLogin';

/**
 * Aviso de que la sesión dejó de servir (#168).
 *
 * <p>Bloquea, pero <b>no desmonta la app</b>: detrás se sigue viendo lo que el usuario estaba
 * haciendo. Es la diferencia con la pantalla de `AutoLogin`, que es para quien nunca llegó a
 * entrar y por lo tanto no tiene nada atrás que preservar.
 *
 * <p>Quién decide mostrarlo es `AutoLogin`, el único dueño de esa presentación. Este componente
 * sólo presenta y ofrece la salida.
 */
const SessionExpiredModal = ({ open }) => {
  const restartLogin = useRestartLogin();
  const [saliendo, setSaliendo] = useState(false);
  const [fallo, setFallo] = useState(null);

  // El aviso se dibuja por portal en <body>, así que la app sigue viva en #root: visible detrás
  // —que es lo que queremos— pero también tabulable, clickeable y accesible por lector de
  // pantalla. `inert` la deja mirable y no operable de una sola vez, y de paso cubre los overlays
  // con z-index literal que no usan los tokens del sistema (#171).
  useEffect(() => {
    if (!open) return undefined;
    const raiz = document.getElementById('root');
    if (!raiz) return undefined;
    raiz.setAttribute('inert', '');
    raiz.setAttribute('aria-hidden', 'true');
    return () => {
      raiz.removeAttribute('inert');
      raiz.removeAttribute('aria-hidden');
    };
  }, [open]);

  const volverAIngresar = () => {
    if (saliendo) return;
    setSaliendo(true);
    setFallo(null);
    Promise.resolve()
      .then(restartLogin)
      .catch((err) => {
        // Dejar el botón bloqueado acá sería un callejón sin salida: el mismo defecto que
        // arregló #136, reintroducido por otra puerta. Si el IdP no contesta, el usuario
        // tiene que poder reintentar y ver por qué falló.
        setFallo(err?.message || String(err));
        setSaliendo(false);
      });
  };

  return (
    <Modal
      open={open}
      onClose={() => {}}
      title="Tu sesión caducó"
      size="sm"
      level="sessionBlock"
      closeOnEsc={false}
      closeOnBackdrop={false}
      footer={(
        <>
          {/* Primero el inocuo: es el que recibe el foco inicial del primitive, y al
              despertar la laptop el usuario aprieta Enter por reflejo. Recargar no
              destruye nada; redirigir al IdP es irreversible. */}
          <Button variant="secondary" onClick={() => window.location.reload()}>
            Recargar la página
          </Button>
          <Button variant="primary" disabled={saliendo} onClick={volverAIngresar}>
            {saliendo ? 'Redirigiendo…' : 'Volver a ingresar'}
          </Button>
        </>
      )}
    >
      {/* Un solo mensaje, porque la marca ya garantiza el hecho: el 401 sólo marca la sesión
          cuando el token efectivamente venció (ver useApiClient), y los otros dos motivos son
          vencimiento por definición. Sin esa garantía habría que hablar en condicional. */}
      Tu sesión caducó y no pudimos renovarla.
      {fallo && (
        <p style={{ marginTop: 12, color: '#ef4444' }}>
          No se pudo volver al login: {fallo}
        </p>
      )}
    </Modal>
  );
};

export default SessionExpiredModal;
